import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import analyticsUtils from 'analytics/utils';
import { breakpoints } from 'style/config';
import locationUtils from 'utils/Location';

const { LINK_TRACKING_EVENT, ASYNC_PAGE_LOAD, PRODUCT_STRING_TEMPLATE } = anaConsts;
const { MY_LISTS, MY_LIST } = anaConsts.PAGE_DETAIL;
const { LOVES } = anaConsts.EVENT_NAMES;
const { USER_PROFILE, MY_LISTS_FLYOUT } = anaConsts.PAGE_TYPES;
const { TOP_NAV, ACCOUNT, LOVES_ICON, MY_LISTS: MY_LISTS_NAV } = anaConsts.NAV_PATH;

class MyListsBindings {
    static DEFAULT_LOVES_LIST_NAME = 'Loves';

    static triggerLinkAnalytics = eventData => {
        processEvent.process(LINK_TRACKING_EVENT, { data: eventData });
    };

    static triggerAsyncPageLoadAnalytics = eventData => {
        processEvent.process(ASYNC_PAGE_LOAD, { data: eventData });
    };

    static pageLoad = ({ pageType, eventName }) => {
        MyListsBindings.triggerAsyncPageLoadAnalytics({
            pageName: `${pageType}:${MY_LISTS}:${eventName}:*`
        });
    };

    static deleteList = () => {
        MyListsBindings.triggerLinkAnalytics({
            pageName: `${USER_PROFILE}:${MY_LIST}:n/a:*`,
            actionInfo: `${MY_LISTS}:${LOVES.DELETE_LIST}`
        });
    };

    static buildProductString = ({ skuId }) => PRODUCT_STRING_TEMPLATE.replace(/{sku}/g, skuId);

    static pageLoadMyListModal = ({ pageType, eventName }) => {
        MyListsBindings.triggerAsyncPageLoadAnalytics({
            pageName: `${pageType}:${MY_LIST}:${eventName}:*`
        });
    };

    static addToLove = ({ skuId, eventPayload }) => {
        MyListsBindings.triggerLinkAnalytics({
            ...eventPayload,
            pageName: `${USER_PROFILE}:${MY_LIST}:${LOVES.MY_LISTS_MODAL}:*`,
            eventStrings: [anaConsts.Event.EVENT_27],
            productStrings: MyListsBindings.buildProductString({ skuId })
        });
    };

    static removeFromLove = ({ skuId, eventPayload }) => {
        MyListsBindings.triggerLinkAnalytics({
            eventStrings: [anaConsts.Event.EVENT_28],
            productStrings: MyListsBindings.buildProductString({ skuId }),
            ...eventPayload
        });
    };

    static addToList = ({ skuId, eventPayload }) => {
        MyListsBindings.triggerLinkAnalytics({
            ...eventPayload,
            eventStrings: [anaConsts.Event.ADD_TO_LIST],
            productStrings: MyListsBindings.buildProductString({ skuId })
        });
    };

    static removeFromList = ({ skuId, eventPayload }) => {
        MyListsBindings.triggerLinkAnalytics({
            eventStrings: [anaConsts.Event.REMOVE_FROM_LIST],
            productStrings: MyListsBindings.buildProductString({ skuId }),
            ...eventPayload
        });
    };

    static triggerErrorAnalytics = ({ errorMessage }) => {
        MyListsBindings.triggerLinkAnalytics({
            fieldErrors: ['list'], //prop28
            errorMessages: errorMessage //prop48
        });
    };

    static formatShareableLists = (lists = []) => {
        if (!Array.isArray(lists) || lists.length === 0) {
            return null;
        }

        const summary = {
            shoppingListItemsCount: [],
            shoppingListName: [],
            isDefault: [],
            shoppingListId: [],
            skuIds: [],
            lists: lists,
            listsCount: lists.length
        };

        for (const list of lists) {
            summary.shoppingListItemsCount.push(list.shoppingListItemsCount ?? 0);
            summary.shoppingListName.push(list.shoppingListName ?? '');
            summary.isDefault.push(list.isDefault ?? false);
            summary.shoppingListId.push(list.shoppingListId ?? '');

            const skuIdList = (list.shoppingListItems ?? []).map(item => item?.sku?.skuId).filter(Boolean);

            if (skuIdList.length > 0) {
                summary.skuIds.push(skuIdList.join(';'));
            }
        }

        return {
            shoppingListItemsCount: summary.shoppingListItemsCount.join(';'),
            shoppingListName: summary.shoppingListName.join(';'),
            isDefault: summary.isDefault.join(';'),
            shoppingListId: summary.shoppingListId.join(';'),
            skuIds: summary.skuIds.join(';'),
            listsCount: summary.listsCount,
            lists: summary.lists
        };
    };

    // Trigger STAG page load event for shareable lists
    static stagShareableListPageLoadEvent = (myLists = {}, displayedSkuIds = null) => {
        // Clear by default to avoid leaking values from a previous route when lists aren't ready/available yet
        digitalData.page.attributes.productStrings = null;

        if (myLists && Object.keys(myLists).length === 0) {
            digitalData.page.shareableList = {};
            digitalData.page.category.pageType = USER_PROFILE;
            digitalData.page.pageInfo.pageName = '';

            return;
        }

        const pathname = locationUtils.getLocation(true)?.pathname || '';
        const targetId = pathname.split('/').filter(Boolean).pop() || null;
        // isMultiListPayload distinguishes the landing page multi-list payload from a single list object
        // (e.g. shareable-list token routes). Kept as a named variable because it gates two branches below.
        const isMultiListPayload = Array.isArray(myLists?.allLoves);
        const allLoves = isMultiListPayload ? myLists.allLoves : myLists ? [myLists] : [];
        let customList = [];

        if (isMultiListPayload && targetId && targetId !== 'Lists') {
            // Filter for the specific list ID only for the multi-list payload
            customList = allLoves.filter(list => list.shoppingListId === targetId);
        } else {
            customList = [...allLoves];
        }

        if (customList.length === 0) {
            digitalData.page.shareableList = {};
            digitalData.page.category.pageType = USER_PROFILE;
            digitalData.page.pageInfo.pageName = targetId === 'Lists' ? MY_LISTS : '';

            return;
        }

        if (customList.length > 0) {
            const shareableData = MyListsBindings.formatShareableLists(customList);

            if (shareableData) {
                digitalData.page.shareableList = {
                    ...(digitalData.page.shareableList || {}),
                    ...shareableData
                };
            }

            const isCustomList = customList[0].shoppingListName !== MyListsBindings.DEFAULT_LOVES_LIST_NAME;
            digitalData.page.category.pageType = USER_PROFILE;
            const pageDetail = isCustomList ? LOVES.LIST_DETAIL_PAGE_NAME : LOVES.LIST_DEFAULT_PAGE_NAME;
            const isProfileListsDetailPage = locationUtils.isProfileListsDetailPage();

            let pageName;

            if (isProfileListsDetailPage) {
                pageName = pageDetail;
                digitalData.page.attributes.productStrings = null;
            } else {
                // MY_LISTS ('my lists') is the short pageName for digitalData; the full LIST_HOME
                // pageName is used only in the ASYNC_PAGE_LOAD processEvent payload.
                pageName = MY_LISTS;

                if (targetId === 'Lists') {
                    const uniqueSkuIds =
                        displayedSkuIds == null
                            ? [...new Set(customList.flatMap(list => (list.shoppingListItems ?? []).map(item => item?.sku?.skuId).filter(Boolean)))]
                            : [...new Set(displayedSkuIds.filter(Boolean))];

                    digitalData.page.attributes.productStrings =
                        uniqueSkuIds.length > 0 ? MyListsBindings.buildMultiProductString({ skuIds: uniqueSkuIds }) : null;
                } else {
                    digitalData.page.attributes.productStrings = null;
                }
            }

            digitalData.page.pageInfo.pageName = pageName;
        }
    };

    // Set where the user is right now as navigationInfo so that
    // we can use it in the next page load as eVar64
    static setNextPageLoadAnalyticsData = (...args) => {
        const navigationInfo = analyticsUtils.buildNavPath([...args]);
        analyticsUtils.setNextPageData({ navigationInfo });
    };

    static setFlyoutNextPage = () => {
        MyListsBindings.setNextPageLoadAnalyticsData(TOP_NAV, ACCOUNT, MY_LISTS_NAV, MY_LISTS_NAV, MY_LISTS_NAV);
    };

    static setLoveIconNextPage = () => {
        MyListsBindings.setNextPageLoadAnalyticsData(TOP_NAV, LOVES_ICON, LOVES_ICON, LOVES_ICON, LOVES_ICON);
    };

    static heartClick = () => {
        const smallViewport = !window.matchMedia(breakpoints.smMin).matches;
        const pageType = USER_PROFILE;
        const pageDetail = MY_LISTS_FLYOUT;
        const pageSection = MY_LISTS;

        if (smallViewport) {
            MyListsBindings.setFlyoutNextPage();

            const pageName = `${pageType}:${pageSection}:${pageDetail}:*`;
            const previousPageName = digitalData.page.attributes.sephoraPageInfo.pageName;
            MyListsBindings.triggerAsyncPageLoadAnalytics({
                pageName,
                pageType,
                pageDetail,
                previousPageName
            });
        } else {
            MyListsBindings.setLoveIconNextPage();
        }
    };

    static createNewListModalPageLoad = () => {
        MyListsBindings.pageLoad({
            pageType: USER_PROFILE,
            eventName: LOVES.CREATE_NEW_LIST_PAGE_LOAD
        });
    };

    static limitReached = () => {
        MyListsBindings.pageLoad({
            pageType: USER_PROFILE,
            eventName: LOVES.LIST_LIMIT_REACHED
        });
    };

    static createListSuccess = () => {
        MyListsBindings.triggerLinkAnalytics({
            pageName: `${USER_PROFILE}:${MY_LISTS}:${LOVES.CREATE_NEW_LIST_PAGE_LOAD}:*`,
            actionInfo: `${MY_LISTS}:${LOVES.CREATE_LIST}`,
            eventStrings: [anaConsts.Event.CREATE_LIST]
        });
    };

    static createListError = error => {
        MyListsBindings.triggerLinkAnalytics({
            pageName: `${USER_PROFILE}:${MY_LISTS}:${LOVES.CREATE_NEW_LIST_PAGE_LOAD}:*`,
            errorMessages: error,
            fieldErrors: anaConsts.CONTEXT.LIST
        });
    };

    static cancelFlowListsModal = () => {
        MyListsBindings.triggerLinkAnalytics({
            pageName: `${USER_PROFILE}:${MY_LISTS}:${LOVES.MY_LISTS_MODAL}:*`,
            actionInfo: `${MY_LISTS}:${LOVES.CANCEL_MODAL}`
        });
    };

    static buildMultiProductString = ({ skuIds }) => {
        const max = skuIds.slice(0, 60);

        return max.map(id => PRODUCT_STRING_TEMPLATE.replace(/{sku}/g, id)).join(',');
    };

    static pageLoadListDetail = ({
        skuIds, isDefault, shoppingListId, shoppingListName, shoppingListItemsCount, isCustomList
    }) => {
        const pageName = isDefault ? LOVES.LIST_DEFAULT_PAGE_NAME : LOVES.LIST_DETAIL_PAGE_NAME;

        MyListsBindings.triggerAsyncPageLoadAnalytics({
            pageName,
            productStrings: MyListsBindings.buildMultiProductString({ skuIds }),
            shoppingListSkuIds: (skuIds || []).join(';'),
            shoppingListId,
            shoppingListName,
            shoppingListItemsCount,
            isCustomList
        });
    };

    static pageLoadShareList = ({ pageType, eventName }) => {
        MyListsBindings.triggerAsyncPageLoadAnalytics({
            pageName: `${pageType}:${eventName}:n/a:*`
        });
    };

    static trackCopyLink = ({ skuIds }) => {
        MyListsBindings.triggerLinkAnalytics({
            actionInfo: `${MY_LISTS}:${LOVES.SHARE_LINK}`,
            productStrings: MyListsBindings.buildMultiProductString({ skuIds })
        });
    };

    static showMoreProductsAnalytics = ({ newSkuIds }) => {
        MyListsBindings.triggerLinkAnalytics({
            pageName: LOVES.LIST_HOME,
            actionInfo: LOVES.MY_LISTS_SHOW_MORE, // prop55 = my lists landing:show more
            productStrings: newSkuIds.length ? MyListsBindings.buildMultiProductString({ skuIds: newSkuIds }) : null
        });
    };
}

export default MyListsBindings;
